import React, { useCallback, useState } from 'react';
import { useTranslate } from 'react-translate-plus';
import styled from 'styled-components';

import {
  CheckboxField,
  CircularButton,
  ContextMenu,
  ContextMenuLink,
  Icon,
  icons,
  PositioningPortalWithState,
  Tooltip,
} from '@hoffmann/pattern-library-2';

import { CustomerInformationModal } from '../../../../components/CustomerInformationModal/CustomerInformationModal';
import { useAccess } from '../../../../hooks/useAccess';
import { useApp } from '../../../../hooks/useApp';
import { ConfigurationState } from '../../../../interfaces/ConfigurationState';
import { toShortDateFromString } from '../../../../projects/utils/date';
import { isAdminOrSales, isDigitalProductAdminUser } from '../../../../services/UserRole';
import { withStyledClasses } from '../../../../styled/withStyledClasses';
import { defaultPositionStrategy } from '../../../../utils/positioningPortalUtils';
import { ConfigurationWithRender } from './GroupView';

type ProjectDto = SimpleProjectServiceAPI.ProjectDto;
type ConfigurationDto = SimpleProjectServiceAPI.ConfigurationDto;
type ConfigurationRequestDto = SimpleProjectServiceAPI.ConfigurationRequestDto;

interface Props {
  t: (key: string, placeholder?: any) => string;
  isSelectionMode: boolean;
  request: ConfigurationRequestDto;
  configurations: ConfigurationWithRender[];
  project?: ProjectDto;
  fullscreen?: boolean;
  indented?: boolean;
  nospace?: boolean;
  checked?: boolean;
  isAccordionOpened?: boolean;
  downloadGLB?: (configurations: ConfigurationDto[], projectCode: string) => void;
  downloadProjectPdf?: (
    configurations: ConfigurationDto[],
    project?: ProjectDto,
    request?: ConfigurationRequestDto,
  ) => void;
  onSelect: (value: boolean) => void;
}

export const GroupItem: React.FC<Props> = ({
  t,
  isSelectionMode,
  checked = false,
  request,
  configurations,
  fullscreen,
  project,
  indented,
  nospace,
  isAccordionOpened,
  downloadGLB,
  downloadProjectPdf,
  onSelect,
}) => {
  const { roles } = useApp();
  const isNormalUser = !isAdminOrSales(roles);

  const { isSalesAccessingSameDistributorRequest } = useAccess();

  const isAccessingSameDistribution = isSalesAccessingSameDistributorRequest(request);
  const active = isNormalUser || isDigitalProductAdminUser(roles) || isAccessingSameDistribution;

  const [infoDialogOpen, setInfoDialogOpen] = useState<boolean>(false);
  const [isOpen, setOpened] = useState<boolean>(isAccordionOpened || active);
  const { loggedIn } = useApp();
  const tModal = useTranslate('CustomerInformationModal');
  const tExport = useTranslate('ExportButton');

  const toggle = () => setOpened(!isOpen);

  const getRequestTitle = () => {
    const subsidiary =
      loggedIn && request.distributor
        ? t('SENT_TO_SUBSIDIARY', {
            distributor: request.distributorDisplayKey
              ? request.distributorDisplayKey
              : request.distributor,
            distributorName: request.distributorDisplayName
              ? request.distributorDisplayName
              : request.distributorName,
          })
        : '';
    switch (configurations[0].configuration.stateCode) {
      case ConfigurationState.OFFER_CREATED:
        return (
          <StateColorTitle state={ConfigurationState.OFFER_CREATED} active={active}>
            {t('SAP_OFFER_NUMBER')}: {request?.sapOffer?.offerNumber}
          </StateColorTitle>
        );
      case ConfigurationState.ORDERED:
        return (
          <StateColorTitle state={ConfigurationState.ORDERED} active={active}>
            {t('ORDER_NUMBER')}: {request?.sapOffer?.orderNumber}
          </StateColorTitle>
        );
      case ConfigurationState.ARCHIVED:
        return (
          <StateColorTitle state={ConfigurationState.ARCHIVED}>{t('ARCHIVED')}</StateColorTitle>
        );
      default:
        return (
          <StateColorTitle state={ConfigurationState.REQUESTED} active={active}>
            {t('REQUESTED_AT', { date: toShortDateFromString(request.createdAt) })}
            {!isNormalUser && subsidiary && (
              <>
                <br />
                {subsidiary}
                <br />
                {request.distributorName === 'No assigned distributor' &&
                  `(${t('SENT_TO_FALLBACK_EMAIL')})`}
              </>
            )}
          </StateColorTitle>
        );
    }
  };

  const handleRequestSelect = useCallback(
    (_: boolean, e: React.MouseEvent) => {
      e.preventDefault();
      onSelect(!checked);
    },
    [checked, onSelect],
  );

  return (
    <RequestGroup fullscreen={fullscreen} nospace={nospace}>
      <Accordion onClick={() => toggle()} indented={indented}>
        {isSelectionMode && (
          <ItemCheckbox checked={checked} disabled={false} onChange={handleRequestSelect} />
        )}
        {getRequestTitle()}
        {loggedIn && active && (
          <Tooltip preventTabbing preferredPosition="top" content={tModal('TITLE')}>
            <CircularButton
              icon={<Icon source={icons.Mail} />}
              size={200}
              ghost
              onClick={(e) => {
                e.stopPropagation();
                setInfoDialogOpen(true);
              }}
            />
          </Tooltip>
        )}
        <SideButtons>
          {(downloadGLB || downloadProjectPdf) && (
            <PositioningPortalWithState
              portalContent={({ close }) => (
                <ContextMenu onClose={close}>
                  {downloadGLB ? (
                    <ContextMenuLink
                      key="download"
                      label={t('GROUPED_DOWNLOAD')}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadGLB(
                          configurations.map(({ configuration }) => configuration),
                          project?.code,
                        );
                      }}
                      icon={<Icon source={icons.GetApp} />}
                    />
                  ) : (
                    <></>
                  )}
                  {downloadProjectPdf ? (
                    <ContextMenuLink
                      key="download_pdf"
                      label={t('PARTIAL_REQUEST_DOWNLOAD')}
                      onClick={async (e) => {
                        e.stopPropagation();
                        await downloadProjectPdf(
                          configurations.map(({ configuration }) => configuration),
                          project,
                          request,
                        );
                      }}
                      icon={<Icon source={icons.GetApp} />}
                    />
                  ) : (
                    <></>
                  )}
                </ContextMenu>
              )}
              positionStrategy={defaultPositionStrategy()}
            >
              {({ open }) => (
                <Tooltip
                  preventTabbing
                  preferredPosition="top"
                  content={tExport('TOOLTIP_BUTTON_DOWNLOAD')}
                >
                  <CircularButton
                    icon={<Icon source={icons.GetApp} />}
                    size={200}
                    ghost
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                  />
                </Tooltip>
              )}
            </PositioningPortalWithState>
          )}
          <CircularButton
            icon={<Icon source={isOpen ? icons.ExpandLess : icons.ExpandMore} />}
            size={200}
            ghost
          />
        </SideButtons>
      </Accordion>
      {isOpen && configurations.map((config) => config.render)}
      {infoDialogOpen && (
        <CustomerInformationModal
          configurationState={configurations[0].configuration.stateCode}
          requestInfo={request}
          onClose={() => setInfoDialogOpen(false)}
        />
      )}
    </RequestGroup>
  );
};

const RequestGroup = styled.div<{
  fullscreen?: boolean;
  nospace?: boolean;
}>`
  ${({ fullscreen, theme, nospace }) => `
    border: 1px solid ${theme.colors.layout40};
    border-left: ${fullscreen ? 'none' : `1px solid ${theme.colors.layout40}`};
    border-right: ${fullscreen ? 'none' : `1px solid ${theme.colors.layout40}`};
    ${!nospace ? 'margin-bottom: 16px;' : ''}
  `}
`;

const Accordion = styled.div<{ indented: boolean }>`
  padding: 8px 16px 8px 20px;
  ${({ indented }) => (indented ? 'padding-left: 48px;' : '')}
  font-size: 16px;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  &:hover {
    background: ${({ theme }) => theme.colors.layout30};
    cursor: pointer;
  }
`;

const SideButtons = styled.div`
  margin-left: auto;
  display: flex;
`;

const ItemCheckbox = styled(withStyledClasses(CheckboxField))`
  padding-right: 16px;
`;

const StateColorTitle = styled.div<{
  state: string;
  active?: boolean;
}>`
  color: ${({ theme, state, active }) =>
    !active ? theme.colors.buttonIconColor : theme.colors['STATE_' + state]};
`;
